/**
 * Address search with Google suggestions — shared by the shop bundle and the Relay back office.
 *
 * Talks ONLY to our own Worker (`/api/shop/address/*` for the shop, `/api/address/*` for Relay),
 * which holds the Google key and the abuse guards (src/address-autofill.ts). This file never sees
 * a key and never loads a Google script.
 *
 * - Debounced (~300 ms), nothing sent under 3 characters, the previous request aborted.
 * - One session token per address-entry session, renewed after a pick (Google bills the
 *   autocomplete calls of a session with its closing details call).
 * - WAI-ARIA 1.2 combobox: ↑/↓ move, Enter picks, Esc closes; "Powered by Google" under the list.
 * - Degrades silently: no key, the daily cap, Google down, or a network error — it is just an
 *   input, and every field it fills stays editable.
 *
 * Two modes: CONTROLLED (`value` + `onChange`) — the street field itself; or STANDALONE (no
 * `value`) — a "find an address" helper above a free-text address box, hidden once the service
 * says it is unavailable.
 */
import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import "./address-search.css";

export interface PickedAddress {
  street: string;
  unit: string;
  suburb: string;
  city: string;
  province: string;
  postcode: string;
  formatted: string;
}
interface Suggestion { id: string; main: string; secondary: string }

/** The picked address as lines, for a free-text address box. */
export function addressLines(a: PickedAddress): string {
  return [a.unit, a.street, a.suburb, a.city, [a.province, a.postcode].filter(Boolean).join(" ")].filter((l) => l.trim()).join("\n");
}

function newSessionToken(): string {
  try {
    if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
    const b = crypto.getRandomValues(new Uint8Array(16));
    return [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  } catch {
    return `s${Date.now().toString(36)}${Math.floor(Math.random() * 1e9).toString(36)}`;
  }
}

export function AddressSearch(props: {
  /** "/api/shop/address" (public shop) or "/api/address" (signed-in Relay). */
  endpoint: string;
  onPick: (a: PickedAddress) => void;
  label: string;
  value?: string;
  onChange?: (v: string) => void;
  className?: string;
  style?: CSSProperties;
  labelClassName?: string;
  inputClassName?: string;
  inputStyle?: CSSProperties;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  disabled?: boolean;
}) {
  const { endpoint, onPick, value, onChange } = props;
  const controlled = value !== undefined;
  const [own, setOwn] = useState("");
  const text = controlled ? value : own;
  const [items, setItems] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  /** The service said no key / cap reached: stop asking for this page view. */
  const [off, setOff] = useState(false);
  /** Only a keystroke asks Google — never a value set by a pick or by the parent. */
  const typed = useRef(false);
  const session = useRef<string | null>(null);
  const inflight = useRef<AbortController | null>(null);
  const uid = useId();
  const inputId = `as-${uid}`;
  const listId = `as-list-${uid}`;

  useEffect(() => {
    if (!typed.current || off) return;
    const q = text.trim();
    if (q.length < 3) { setItems([]); setOpen(false); return; }
    const t = window.setTimeout(async () => {
      inflight.current?.abort();
      const ac = new AbortController();
      inflight.current = ac;
      session.current ??= newSessionToken();
      try {
        const r = await fetch(`${endpoint}/suggest`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ q, session: session.current }),
          signal: ac.signal,
        });
        const d = r.ok ? ((await r.json()) as { available?: boolean; reason?: string; suggestions?: Suggestion[] }) : null;
        if (ac.signal.aborted) return;
        if (!d?.available) {
          if (d?.reason === "no-key" || d?.reason === "daily-cap") setOff(true);
          setItems([]); setOpen(false);
          return;
        }
        setItems(d.suggestions ?? []);
        setActive(-1);
        setOpen((d.suggestions ?? []).length > 0);
      } catch {
        if (!ac.signal.aborted) { setItems([]); setOpen(false); }
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [text, endpoint, off]);

  useEffect(() => () => inflight.current?.abort(), []);

  const setText = (v: string) => {
    typed.current = true;
    if (controlled) onChange?.(v);
    else setOwn(v);
  };

  const pick = async (s: Suggestion) => {
    setOpen(false);
    setItems([]);
    inflight.current?.abort();
    const token = session.current ?? newSessionToken();
    session.current = null; // the session ends with this details call
    typed.current = false;
    try {
      const r = await fetch(`${endpoint}/details`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ id: s.id, session: token }),
      });
      const d = r.ok ? ((await r.json()) as { available?: boolean; reason?: string; address?: PickedAddress }) : null;
      if (d?.available && d.address) {
        if (!controlled) setOwn("");
        onPick(d.address);
        return;
      }
      if (d?.reason === "no-key" || d?.reason === "daily-cap") setOff(true);
    } catch {
      /* fall through: keep what was typed */
    }
    // Details unavailable: the suggestion's own text is still a better street than the fragment typed.
    if (controlled) onChange?.(s.main);
  };

  if (off && !controlled) return null;
  const expanded = open && items.length > 0 && !off;

  return (
    <div className={`as-wrap ${props.className ?? ""}`} style={props.style}>
      <label htmlFor={inputId} className={props.labelClassName}><span>{props.label}</span></label>
      <input
        id={inputId}
        className={props.inputClassName}
        style={props.inputStyle}
        value={text}
        placeholder={props.placeholder}
        autoComplete={props.autoComplete ?? "off"}
        required={props.required}
        disabled={props.disabled}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={expanded}
        aria-controls={listId}
        aria-activedescendant={expanded && active >= 0 ? `${listId}-${active}` : undefined}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onFocus={() => { if (items.length) setOpen(true); }}
        onKeyDown={(e) => {
          if (!expanded) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i + 1) % items.length); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i <= 0 ? items.length - 1 : i - 1)); }
          else if (e.key === "Enter" && active >= 0) { e.preventDefault(); void pick(items[active]!); }
          else if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
        }}
      />
      <div className="as-pop" hidden={!expanded}>
        <ul id={listId} role="listbox" aria-label="Address suggestions" className="as-list">
          {items.map((s, i) => (
            <li
              key={s.id}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              className={`as-opt${i === active ? " as-on" : ""}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => void pick(s)}
            >
              <b className="as-main">{s.main}</b>
              {s.secondary && <small className="as-sec">{s.secondary}</small>}
            </li>
          ))}
        </ul>
        <div className="as-attrib" aria-hidden="true">Powered by Google</div>
      </div>
      <div className="as-sr" role="status" aria-live="polite">{expanded ? `${items.length} address suggestion${items.length === 1 ? "" : "s"}. Use the arrow keys to choose.` : ""}</div>
    </div>
  );
}
